<script setup>
import { Head, Link, usePage } from '@inertiajs/vue3';
import { computed, ref, defineAsyncComponent, inject } from 'vue';
import ContactForm from '@/Components/ContactForm.vue';
const ShawafBrands = defineAsyncComponent(() => import("@/Components/ShawafLanding/ShawafBrands.vue"));

const page = usePage();
const dir = computed(() => page.props.locale === 'ar' ? 'rtl' : 'ltr');

const categories = [
    { nameKey: 'brand_alfanan', count: 3, active: false, link: 'brands.alfanan' },
    { nameKey: 'brand_alafarah', count: 8, active: false, link: 'brands.alafarah' },
    { nameKey: 'brand_cleanotic', count: 1, active: false, link: 'brands.cleanotic' },
    { nameKey: 'brand_queen', count: 1, active: true, link: 'brands.queen' },
    { nameKey: 'brand_wafi', count: 1, active: false, link: 'brands.wafi' },
];

const products = [
    {
        id: 1,
        titleKey: 'product_queen_automatic_title',
        descKey: 'product_queen_automatic_desc',
        image: '/assets/brands/queen/product-queen.png',
        link: '#'
    }
];

const bubbles = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    size: Math.random() * 15 + 8 + 'px',
    left: Math.random() * 90 + 5 + '%',
    animationDuration: Math.random() * 4 + 6 + 's',
    animationDelay: Math.random() * 5 + 's'
}));

const __t = inject('__t');

const parseProductData = (descKey) => {
    const text = __t(descKey);
    if (!text) return { cleanDesc: '', sizes: [] };

    let cleanDesc = text;
    let sizes = [];

    const sizePattern = /(العبوات متوفرة بحجم|متوفر بعبوة سعة|متوفر بعبوات سعة|متوفر بحجم|متوفر بعبوة|العبوة متوفرة بحجم|العبوة متوفرة بسعة|العبوات متوفرة بحجوم|متوفرة بحجم|متوفرة بعبوة سعة|Available\s+in\s+sizes|Available\s+in\s+capacity)\s*:?\s*([^<.|\n]+)/i;
    const match = text.match(sizePattern);

    if (match) {
        cleanDesc = text.replace(match[0], '').trim();
        sizes = match[2].split(/\s*-\s*|\s*،\s*|\s*,\s*/).map(s => s.trim()).filter(Boolean);
    }
    cleanDesc = cleanDesc.replace(/(<br\s*\/?>|<\/?[^>]+>|\s|،|,|-|:|\.)+$/, '').trim();
    return { cleanDesc, sizes };
};

const processedProducts = computed(() => {
    return products.map(product => {
        const { cleanDesc, sizes } = parseProductData(product.descKey);
        return { ...product, cleanDesc, sizes };
    });
});
</script>

<template>

    <Head :title="__t('brand_queen')" />

    <div class="min-h-screen font-sans overflow-hidden bg-white dark:bg-slate-900" :dir="dir">

        <!-- Hero Section -->
        <header
            class="relative w-full min-h-[85vh] bg-gradient-to-br from-[#FF0080] to-[#7928CA] overflow-hidden flex items-center justify-center pt-28 pb-16 md:pt-32">

            <div class="absolute inset-0 z-0 pointer-events-none overflow-hidden">
                <div v-for="bubble in bubbles" :key="bubble.id"
                    class="absolute bg-white/10 rounded-full animate-float-up" :style="{
                        width: bubble.size,
                        height: bubble.size,
                        left: bubble.left,
                        animationDuration: bubble.animationDuration,
                        animationDelay: bubble.animationDelay,
                        bottom: '-50px'
                    }">
                </div>
            </div>

            <div
                class="absolute inset-0 opacity-10 bg-[url('/assets/brands/queen-bg.png')] bg-cover bg-center mix-blend-overlay">
            </div>

            <div
                class="container mx-auto py-12 px-4 md:px-6 relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">

                <div class="text-center lg:text-start space-y-6 md:space-y-8 order-2 lg:order-1" data-aos="fade-up">
                    <div class="flex flex-col items-center lg:items-start">
                        <h1
                            class="text-5xl md:text-7xl lg:text-8xl font-black text-white drop-shadow-xl tracking-tight mb-2 font-display leading-tight">
                            Queen
                        </h1>
                        <h2 class="text-2xl md:text-4xl font-bold text-white/95">
                            {{ __t('brand_queen') }}
                        </h2>

                        <div
                            class="w-20 h-1.5 md:w-24 md:h-2 bg-white rounded-full my-6 shadow-glow transition-all hover:w-32 duration-300">
                        </div>

                        <p class="text-base md:text-lg lg:text-xl text-white/90 leading-relaxed max-w-lg">
                            {{ __t('queen_about_desc') }}
                        </p>
                    </div>

                    <div class="flex flex-wrap gap-3 mt-10 justify-center lg:justify-start">
                        <Link v-for="cat in categories" :key="cat.nameKey" :href="route(cat.link)"
                            class="px-4 py-1.5 rounded-full text-xs md:text-sm font-semibold backdrop-blur-md transition-all duration-300 border border-white/20 select-none"
                            :class="cat.active ? 'bg-white text-[#7928CA] shadow-[0_0_15px_rgba(255,255,255,0.4)] scale-105' : 'bg-white/10 text-white hover:bg-white/20 hover:scale-105'">
                            {{ __t(cat.nameKey) }}
                        </Link>
                    </div>
                </div>

                <div class="relative w-full h-full flex items-center justify-center lg:justify-end order-1 lg:order-2 mb-8 lg:mb-0"
                    data-aos="zoom-in" data-aos-duration="1000">
                    <div class="relative w-[300px] md:w-[400px] lg:w-full lg:max-w-[480px] aspect-square">
                        <div
                            class="absolute inset-0 bg-white/20 blur-[50px] lg:blur-[70px] rounded-full animate-pulse-slow">
                        </div>

                        <img src="/assets/brands/queen/product-queen.png" alt="Main Product"
                            class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] lg:w-[65%] z-20 object-contain drop-shadow-[0_20px_40px_rgba(0,0,0,0.4)] animate-float">

                        <div
                            class="absolute -top-5 -left-5 md:-top-10 md:-left-10 w-16 h-16 md:w-24 md:h-24 bg-gradient-to-br from-white/30 to-transparent rounded-full backdrop-blur-sm border border-white/20 animate-bounce-slow">
                        </div>
                        <div class="absolute bottom-16 -right-2 md:bottom-20 md:-right-5 w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-white/20 to-transparent rounded-full backdrop-blur-sm border border-white/10 animate-bounce-slow"
                            style="animation-delay: 1s"></div>
                    </div>
                </div>
            </div>

            <div class="absolute bottom-0 left-0 w-full overflow-hidden leading-[0] z-20" style="rotate: 180deg;">
                <svg class="relative block w-[calc(100%+1.3px)] h-[60px] md:h-[120px]" data-name="Layer 1"
                    xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
                    <path
                        d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
                        class="fill-white dark:fill-slate-900"></path>
                </svg>
            </div>
        </header>

        <section class="py-16 md:py-32 relative overflow-hidden">
            <div
                class="absolute right-0 top-0 w-full md:w-1/2 h-full bg-gradient-to-l from-pink-50/50 to-transparent dark:from-slate-800/30 pointer-events-none">
            </div>

            <div class="container mx-auto px-4 md:px-6 relative z-10">
                <div class="text-center mb-12 md:mb-20" data-aos="fade-up">
                    <h2
                        class="text-3xl md:text-5xl font-bold text-slate-800 dark:text-white inline-block relative pb-4">
                        {{ __t('about_us') }}
                        <span
                            class="absolute bottom-0 left-1/2 -translate-x-1/2 w-20 h-1.5 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full"></span>
                    </h2>
                </div>

                <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                    <div class="relative mx-auto w-full max-w-[350px] md:max-w-[400px] aspect-square order-1"
                        data-aos="zoom-in">
                        <div class="relative w-full h-full group">
                            <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"
                                class="absolute inset-0 w-full h-full text-pink-100 dark:text-pink-900/20 -z-10 transform scale-125 md:scale-135 animate-pulse-slow transition-colors duration-500">
                                <path fill="currentColor"
                                    d="M43.8,-76.3C56.6,-70.6,66.8,-60.8,76.5,-50.1C86.1,-39.5,95.3,-28,97,-15.8C98.7,-3.6,92.9,9.4,85.3,21.1C77.7,32.7,68.2,43,57.1,51.8C46,60.6,33.3,67.9,20.2,71.5C7.1,75.1,-6.3,74.9,-19.1,71.2C-31.9,67.4,-44,60,-53.6,50.3C-63.2,40.6,-70.3,28.6,-73.4,15.9C-76.6,3.3,-75.7,-10,-69.8,-21.5C-64,-33,-53.1,-42.7,-41.7,-48.9C-30.3,-55.1,-18.4,-57.8,-5.8,-58.8C6.9,-59.8,25.4,-59.1,38.8,-58.4L43.8,-76.3Z"
                                    transform="translate(100 100)" />
                            </svg>

                            <div
                                class="relative w-full h-full overflow-hidden rounded-[40px] md:rounded-[60px] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] transform md:rotate-2 group-hover:rotate-0 transition-all duration-700 border-[6px] md:border-8 border-white dark:border-slate-800 box-border bg-white dark:bg-slate-800">
                                <img src="/assets/brands/queen/about-1.jpg" alt="Queen"
                                    class="w-full h-full object-cover">
                            </div>

                            <div
                                class="absolute -top-4 -right-4 md:-top-6 md:-right-6 w-20 h-20 md:w-24 md:h-24 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center shadow-xl z-20 animate-bounce-slow">
                                <img src="/assets/brands/queen/about-2.png" alt="Logo"
                                    class="w-12 h-12 md:w-16 md:h-16 object-contain" />
                            </div>
                        </div>
                    </div>

                    <div class="lg:pl-8 text-center lg:text-start order-2" data-aos="fade-left">
                        <h3 class="text-2xl md:text-4xl font-bold text-slate-800 dark:text-white mb-6">
                            {{ __t('queen_about_title') }}
                        </h3>
                        <div class="prose prose-lg dark:prose-invert text-slate-600 dark:text-slate-300 leading-relaxed mb-8 mx-auto lg:mx-0"
                            v-html="__t('queen_about_desc')">
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <div class="w-full overflow-hidden leading-[0] transform rotate bg-slate-50 dark:bg-slate-800 -mt-1 block">
            <svg class="relative block w-[calc(100%+1.3px)] h-[50px] md:h-[100px]" data-name="Layer 1"
                xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
                <path
                    d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
                    class="fill-white dark:fill-slate-900"></path>
            </svg>
        </div>

        <section id="products" class="py-16 md:py-24 bg-slate-50 dark:bg-slate-800 relative z-10">
            <div class="container mx-auto px-4 md:px-6">
                <div class="text-center mb-16 md:mb-20" data-aos="fade-up">
                    <span
                        class="text-pink-600 dark:text-pink-400 font-bold tracking-widest uppercase text-xs md:text-sm mb-3 block">{{
                            __t('brand_queen') }}</span>
                    <h2 class="text-3xl md:text-5xl font-black text-slate-900 dark:text-white mb-6">
                        {{ __t('queen_products_title') }}
                    </h2>
                </div>

                <div
                    class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8 lg:gap-10 justify-center">
                    <div v-for="(product, index) in processedProducts" :key="product.id"
                        class="group relative bg-white dark:bg-slate-900 rounded-[2rem] p-6 shadow-lg hover:shadow-2xl hover:translate-y-[-5px] transition-all duration-300 overflow-hidden border border-gray-100 dark:border-slate-700 flex flex-col"
                        data-aos="fade-up" :data-aos-delay="index * 50">
                        <div
                            class="absolute inset-0 bg-gradient-to-br from-pink-500/5 to-rose-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                        </div>
                        <div
                            class="absolute -right-16 -top-16 w-32 h-32 bg-pink-50 dark:bg-slate-800 rounded-full group-hover:scale-150 transition-transform duration-500 pointer-events-none">
                        </div>
                        <div class="relative w-full aspect-[4/5] mb-6 flex items-center justify-center p-4">
                            <div
                                class="absolute w-2/3 h-2/3 bg-pink-200/20 rounded-full blur-2xl group-hover:bg-pink-300/30 transition-colors">
                            </div>
                            <img :src="product.image" :alt="__t(product.titleKey)"
                                class="w-full h-full object-contain drop-shadow-lg z-10 transform group-hover:scale-110 transition-transform duration-500">
                        </div>
                        <div class="text-center mt-auto relative z-10">
                            <h3
                                class="text-lg md:text-xl font-bold text-slate-800 dark:text-white mb-3 group-hover:text-pink-600 transition-colors leading-tight">
                                <Link :href="product.link">{{ __t(product.titleKey) }}</Link>
                            </h3>
                            <div
                                class="w-10 h-1 bg-gray-100 dark:bg-slate-700 mx-auto rounded-full mb-3 group-hover:bg-pink-600 transition-colors">
                            </div>
                            <p class="text-xs md:text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-4"
                                v-html="product.cleanDesc">
                            </p>
                            <div v-if="product.sizes.length" class="flex flex-wrap justify-center gap-2 mt-2 mb-4">
                                <span v-for="size in product.sizes" :key="size"
                                    class="px-3 py-1 bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400 text-[10px] md:text-xs font-bold rounded-full border border-pink-100 dark:border-pink-800 transition-all duration-300 hover:scale-105">
                                    {{ size }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <ShawafBrands />
        <ContactForm />
    </div>
</template>

<style scoped>
.font-display {
    font-family: 'Outfit', sans-serif;
}

@keyframes float {

    0%,
    100% {
        transform: translateY(0) rotate(0deg);
    }

    50% {
        transform: translateY(-15px) rotate(2deg);
    }
}

@keyframes float-delayed {

    0%,
    100% {
        transform: translateY(0) rotate(12deg);
    }

    50% {
        transform: translateY(-10px) rotate(10deg);
    }
}

@keyframes float-delayed-2 {

    0%,
    100% {
        transform: translateY(0) rotate(-12deg);
    }

    50% {
        transform: translateY(-8px) rotate(-10deg);
    }
}

@keyframes float-up {
    0% {
        transform: translateY(0) scale(1);
        opacity: 0;
    }

    15% {
        opacity: 0.4;
    }

    100% {
        transform: translateY(-80vh) scale(0.8);
        opacity: 0;
    }
}

.animate-float {
    animation: float 6s ease-in-out infinite;
}

.animate-float-delayed {
    animation: float-delayed 7s ease-in-out infinite;
    animation-delay: 1.5s;
}

.animate-float-delayed-2 {
    animation: float-delayed-2 8s ease-in-out infinite;
    animation-delay: 3s;
}

.animate-float-up {
    animation: float-up linear infinite;
}

.animate-pulse-slow {
    animation: pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.shadow-glow {
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.5);
}
</style>
